import { useMemo, useState } from 'react';
import { Receipt } from 'lucide-react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import {
  Activity,
  ActivityResolutionStatus,
  PendingItemSnapshotEntry,
} from '@models/activity.model';
import { formatActivityTime } from '@utils/formatDate';
import { useFeatureAccess } from '@utils/featureFlag';
import { InvoiceManagerSlider } from '@components/organisms/InvoiceManagerSlider';
import { ActivityHeader } from '../ActivityHeader';
import { useTranslation } from '@hooks/useTranslation';

interface ActivityInvoicePendingProps {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
}

const INVOICING_ROLES: Array<UserRoles> = [
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
  UserRoles.SUPER_ADMIN,
];

export const ActivityInvoicePending = ({
  activity,
  handleOpenTaskDetail,
}: ActivityInvoicePendingProps) => {
  const tActivityCards = useTranslation('activityCards');
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const { canAccess } = useFeatureAccess();

  const snapshot: PendingItemSnapshotEntry[] = useMemo(
    () => activity.pendingItemsSnapshot ?? [],
    [activity.pendingItemsSnapshot],
  );
  const materialCount = useMemo(
    () => snapshot.filter((s) => s.itemType === 'MATERIAL').length,
    [snapshot],
  );
  const equipmentCount = useMemo(
    () => snapshot.filter((s) => s.itemType === 'EQUIPMENT').length,
    [snapshot],
  );
  const totalCount = materialCount + equipmentCount;

  const taskId = activity.taskId || activity.task?.taskId;
  const taskName = activity.task?.name || activity.name || tActivityCards.invoicePending.defaultTaskName;
  const canOpenTask = Boolean(taskId && handleOpenTaskDetail);

  const isResolved = activity.resolutionStatus === ActivityResolutionStatus.RESOLVED;
  // Gate the action behind BOTH the INVOICES feature flag and an invoicing role,
  // so when the feature is off (env kill-switch or role) the button is hidden.
  const canInvoice =
    canAccess('INVOICES') && Boolean(userRole && INVOICING_ROLES.includes(userRole as UserRoles));

  const rightLabel = (
    <p className="text-sm text-gray-500">
      {activity.createdAt ? formatActivityTime(activity.createdAt) : ''}
    </p>
  );

  // Border accent: orange for ACTIVE (action needed), gray for RESOLVED (historical).
  const accentBorderClass = isResolved
    ? 'border-l-[3px] border-l-gray-300'
    : 'border-l-[3px] border-l-amber-500';

  return (
    <>
      <Card className={accentBorderClass}>
        <ActivityHeader activity={activity} rightLabel={rightLabel} />

        <div className="mt-4 space-y-3">
          <div className="flex gap-1 items-baseline">
            <p className={`text-sm ${isResolved ? 'text-gray-400' : 'text-neutral-800'}`}>
              {tActivityCards.projectLabel}
            </p>
            <p
              className={`text-sm font-semibold capitalize ${
                isResolved ? 'text-gray-400' : 'text-neutral-800'
              }`}>
              {activity.project?.name || tActivityCards.notAvailable}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (taskId && handleOpenTaskDetail) handleOpenTaskDetail(taskId);
            }}
            disabled={!canOpenTask}
            className="flex items-center gap-2 text-left group disabled:cursor-default">
            <Receipt
              className={isResolved ? 'text-gray-400' : 'text-blue-800'}
              size={20}
            />
            <p
              className={`text-base font-semibold ${
                isResolved ? 'text-gray-500' : 'text-neutral-800'
              } ${canOpenTask ? 'group-hover:underline cursor-pointer' : ''}`}>
              {taskName}
            </p>
          </button>

          <div
            className={`text-sm rounded-lg bg-gray-50 dark:bg-gray-700/30 p-3 ${
              isResolved ? 'text-gray-400' : 'text-neutral-700'
            }`}>
            <p className="font-semibold mb-1">{tActivityCards.invoicePending.pendingToInvoice}</p>
            {totalCount > 0 ? (
              <ul className="space-y-0.5">
                {equipmentCount > 0 && (
                  <li>
                    {equipmentCount}{' '}
                    {equipmentCount === 1
                      ? tActivityCards.invoicePending.equipmentSingular
                      : tActivityCards.invoicePending.equipmentPlural}
                  </li>
                )}
                {materialCount > 0 && (
                  <li>
                    {materialCount}{' '}
                    {materialCount === 1
                      ? tActivityCards.invoicePending.materialSingular
                      : tActivityCards.invoicePending.materialPlural}
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-gray-400">{tActivityCards.invoicePending.noPendingItems}</p>
            )}
          </div>

          <div className="pt-2 flex justify-end">
            {isResolved ? (
              <span className="text-xs text-gray-400">✓ {tActivityCards.invoicePending.resolved}</span>
            ) : canInvoice ? (
              <Button
                variant="primary"
                size="md"
                leftIcon={<Receipt size={14} />}
                onClick={() => setIsSliderOpen(true)}>
                {tActivityCards.invoicePending.createInvoice}
              </Button>
            ) : (
              <span className="text-xs text-gray-500 italic">
                {tActivityCards.invoicePending.awaitingInvoicing}
              </span>
            )}
          </div>
        </div>
      </Card>

      {isSliderOpen && activity.project?.projectId && (
        <InvoiceManagerSlider
          isOpen={isSliderOpen}
          projectId={activity.project.projectId}
          projectName={activity.project?.name}
          defaultRecipientEmail={activity.project?.client?.email}
          onClose={() => setIsSliderOpen(false)}
        />
      )}
    </>
  );
};
